/**
 * The parameter contract for every FX unit type. Imported by BOTH the
 * main-thread mapper (to build ModRoutes) and the worklet units (which must
 * implement exactly these parameter names). Pure data — no DOM, no DSP.
 *
 * All parameters are normalized 0..1; units map to physical ranges internally.
 * `def` is the default normalized value.
 */

export interface UnitParamSpec {
  def: number;
  /** What the parameter controls, with the physical range the unit maps to. */
  doc: string;
}

export interface UnitSpec {
  implemented: boolean;
  params: Record<string, UnitParamSpec>;
}

export const UNIT_CATALOG: Record<string, UnitSpec> = {
  Filter: {
    implemented: true,
    params: {
      cutoff: { def: 0.8, doc: 'cutoff 20 Hz..20 kHz (exp)' },
      reso: { def: 0.2, doc: 'resonance Q 0.5..12' },
      mode: { def: 0, doc: '0=LPF, 0.5=BPF, 1=HPF (switched)' },
      drive: { def: 0.1, doc: 'input drive into soft clip 0..+18 dB' },
      lfoSpeed: { def: 0.3, doc: 'LFO 0.05..20 Hz (exp); BPM-synced when program syncs' },
      lfoDepth: { def: 0, doc: 'LFO→cutoff depth 0..±4 octaves' },
      lfoShape: { def: 0, doc: '0=sine, 0.25=tri, 0.5=saw, 0.75=square, 1=S&H' },
      sens: { def: 0.5, doc: 'envelope→cutoff, bipolar (0.5 = off)' },
      sync: { def: 0, doc: '1 = LFO locks to beat divisions of the transport' },
    },
  },
  Isolator: {
    implemented: true,
    params: {
      sweep: { def: 0.5, doc: 'kill-sweep position: 0=low only, 0.5=flat, 1=high only' },
      depth: { def: 1, doc: 'how far non-selected bands duck (0=no effect, 1=full kill)' },
      low: { def: 0.5, doc: 'low gain: 0=kill, 0.5=unity, 1=+6 dB' },
      mid: { def: 0.5, doc: 'mid gain: 0=kill, 0.5=unity, 1=+6 dB' },
      high: { def: 0.5, doc: 'high gain: 0=kill, 0.5=unity, 1=+6 dB' },
      cutoff: { def: 0.5, doc: 'band-select frequency for cut/solo modes 60 Hz..12 kHz (exp)' },
      level: { def: 0, doc: 'notch depth for cut/solo modes (0 = off)' },
    },
  },
  'Parametric EQ': {
    implemented: true,
    params: {
      freq: { def: 0.5, doc: 'center 60 Hz..16 kHz (exp)' },
      gain: { def: 0.5, doc: '−18..+18 dB (0.5 = flat)' },
      q: { def: 0.4, doc: 'Q 0.4..8' },
    },
  },
  Compressor: {
    implemented: true,
    params: {
      attack: { def: 0.3, doc: 'attack 0.5..100 ms (exp)' },
      release: { def: 0.4, doc: 'release 20..800 ms (exp)' },
      depth: { def: 0.5, doc: 'threshold/ratio macro: 0=off, 1=squash hard' },
    },
  },
  Distortion: {
    implemented: true,
    params: {
      drive: { def: 0.4, doc: 'gain into shaper 0..+40 dB' },
      tone: { def: 0.5, doc: 'post tilt filter dark..bright' },
      level: { def: 0.6, doc: 'output trim with auto gain compensation' },
    },
  },
  Decimator: {
    implemented: true,
    params: {
      rate: { def: 1, doc: 'resample 200 Hz..full rate (exp, 1 = bypassish)' },
      bits: { def: 1, doc: 'bit depth 2..16 (1 = 16 bit)' },
      depth: { def: 1, doc: 'dry/crushed mix inside the unit' },
      tone: { def: 0.7, doc: 'post LPF 500 Hz..20 kHz' },
    },
  },
  'Ring Mod': {
    implemented: true,
    params: {
      freq: { def: 0.4, doc: 'carrier 20 Hz..8 kHz (exp)' },
      mix: { def: 1, doc: 'dry/ring balance inside the unit' },
      spread: { def: 0, doc: 'L/R carrier detune ±10%' },
      lfoSpeed: { def: 0, doc: 'carrier pan LFO 0..8 Hz' },
    },
  },
  Tremolo: {
    implemented: true,
    params: {
      speed: { def: 0.4, doc: '0.1..30 Hz (exp) or synced divisions' },
      depth: { def: 0.8, doc: 'modulation depth 0..1' },
      shape: { def: 0, doc: '0=sine, 0.5=square (slicer), 1=saw' },
      mode: { def: 0, doc: '0=tremolo, 0.5=slicer, 1=auto-pan' },
      length: { def: 0.5, doc: 'slicer gate length 5..95%' },
    },
  },
  Flanger: {
    implemented: true,
    params: {
      time: { def: 0.3, doc: 'base delay 0.1..10 ms (exp)' },
      speed: { def: 0.3, doc: 'LFO 0.02..8 Hz (exp)' },
      depth: { def: 0.5, doc: 'LFO sweep depth' },
      feedback: { def: 0.5, doc: 'feedback 0..0.95' },
      polarity: { def: 1, doc: '0 = negative (jet−), 1 = positive (jet+)' },
    },
  },
  Phaser: {
    implemented: true,
    params: {
      freq: { def: 0.5, doc: 'manual center 100 Hz..8 kHz (exp)' },
      reso: { def: 0.4, doc: 'feedback 0..0.9' },
      speed: { def: 0.3, doc: 'LFO 0.02..8 Hz (exp)' },
      depth: { def: 0, doc: 'LFO sweep depth (0 = manual mode)' },
      step: { def: 0, doc: '0 = continuous, 1 = stepped/random LFO' },
    },
  },
  Chorus: {
    implemented: true,
    params: {
      speed: { def: 0.3, doc: 'LFO 0.05..4 Hz (exp)' },
      depth: { def: 0.5, doc: 'mod depth' },
      mix: { def: 0.7, doc: 'dry/wet inside the unit' },
    },
  },
  Delay: {
    implemented: true,
    params: {
      time: { def: 0.5, doc: 'synced: 1/32..1 bar stepped; free: 20..1400 ms (exp)' },
      depth: { def: 0.5, doc: 'feedback + send macro (KP "Delay Depth")' },
      tone: { def: 0.6, doc: 'LPF in feedback loop 800 Hz..16 kHz' },
      pingpong: { def: 0, doc: '0 = stereo, 1 = ping-pong' },
      sync: { def: 1, doc: '0 = free time, 1 = BPM-synced' },
    },
  },
  'Tape Echo': {
    implemented: true,
    params: {
      time: { def: 0.5, doc: 'synced divisions or 30..1200 ms' },
      depth: { def: 0.5, doc: 'feedback + send macro' },
      tone: { def: 0.5, doc: 'echo tone (LP+HP in loop)' },
      wow: { def: 0.3, doc: 'wow/flutter amount' },
      saturation: { def: 0.4, doc: 'loop saturation' },
      sync: { def: 1, doc: '0 = free, 1 = BPM-synced' },
    },
  },
  Reverb: {
    implemented: true,
    params: {
      time: { def: 0.5, doc: 'decay 0.3..12 s (exp)' },
      depth: { def: 0.5, doc: 'dry/wet inside the unit' },
      damp: { def: 0.5, doc: 'HF damping' },
      predelay: { def: 0.1, doc: '0..120 ms' },
      gate: { def: 0, doc: '0 = off, >0 gate time 40..400 ms' },
    },
  },
  'Early Reflection': {
    implemented: true,
    params: {
      time: { def: 0.5, doc: 'reflection spread 10..300 ms' },
      mix: { def: 0.5, doc: 'dry/reflections balance' },
      reverse: { def: 0, doc: '0 = normal taps, 1 = reverse-ramp taps' },
    },
  },
  'Pitch Shifter': {
    implemented: true,
    params: {
      pitch: { def: 0.5, doc: '−24..+24 semitones (0.5 = unison)' },
      tracking: { def: 0.5, doc: 'quantized pitch travel for stepped programs' },
      mix: { def: 1, doc: 'dry/shifted balance inside the unit' },
      window: { def: 0.5, doc: 'grain window 20..120 ms' },
      lfoSpeed: { def: 0.3, doc: 'vibrato LFO 0.3..12 Hz (exp)' },
      lfoDepth: { def: 0, doc: 'vibrato depth 0..±2 st' },
    },
  },
  Looper: {
    implemented: true,
    params: {
      beat: { def: 0.5, doc: 'loop slice length: 1/16 beat..4 beats (stepped)' },
      speed: { def: 0.75, doc: 'playback rate 0.25..2x (0.75 = 1x)' },
      direction: { def: 1, doc: '0 = reverse, 1 = forward' },
      on: { def: 1, doc: '0 = pass-through, 1 = looping' },
      probability: { def: 1, doc: 'chance a slice repeats (auto-fill roller)' },
    },
  },
  'Vinyl Break': {
    implemented: true,
    params: {
      breakTime: { def: 0.5, doc: 'spin-down time 0.05..4 s' },
      scratch: { def: 0.5, doc: 'bipolar scratch position (0.5 = rest)' },
      engage: { def: 0, doc: '0 = pass-through, 1 = braking' },
    },
  },
  'Grain Shifter': {
    implemented: true,
    params: {
      cycle: { def: 0.5, doc: 'grain re-sample cycle 30 ms..2 s (synced when program syncs)' },
      length: { def: 0.5, doc: 'grain length 5..500 ms' },
      mix: { def: 1, doc: 'dry/grain balance inside the unit' },
    },
  },
  'Analog Osc': {
    implemented: true,
    params: {
      pitch: { def: 0.4, doc: 'osc pitch 20 Hz..4 kHz (exp)' },
      tone: { def: 0.5, doc: 'waveform blend sine..saw..square + filter' },
      crossMod: { def: 0, doc: 'cross/FM modulation amount' },
      decay: { def: 0.5, doc: 'amp decay for percussive trigger 30 ms..2 s' },
      level: { def: 0.7, doc: 'osc level' },
      trigger: { def: 0, doc: 'retrigger on touch (edge-sensitive)' },
    },
  },
  Shimmer: {
    implemented: true,
    params: {
      time: { def: 0.5, doc: 'reverb decay 0.5..10 s (exp)' },
      depth: { def: 0.5, doc: 'dry/shimmer mix inside the unit' },
      damp: { def: 0.4, doc: 'HF damping in the tail' },
      shift: { def: 0.75, doc: 'pitch in the feedback path: 0=−12, 0.5=0, 0.75=+12, 1=+24 st' },
      feedback: { def: 0.6, doc: 'how much shifted tail regenerates (the bloom)' },
    },
  },
  'Graphic EQ': {
    implemented: true,
    params: {
      type: { def: 0, doc: 'preset curve select 0..11 (stepped, per KPV GEQ_Type)' },
      depth: { def: 1, doc: 'flat..full curve blend' },
    },
  },
  'IR Loader': {
    implemented: true,
    params: {
      ir: { def: 0, doc: 'procedural IR select: 0=cheap speaker, ⅓=amp cab, ⅔=megaphone, 1=small room' },
      depth: { def: 1, doc: 'dry/convolved mix inside the unit' },
      tone: { def: 0.5, doc: 'post tilt dark..bright' },
    },
  },
  'Sample Osc': {
    implemented: true,
    params: {
      sample: { def: 0, doc: 'source select: 0=white noise, ⅓=filtered noise, ⅔=snare burst, 1=click' },
      pitch: { def: 0.5, doc: 'playback pitch ±12 st (0.5 = native)' },
      tone: { def: 0.5, doc: 'noise color / filter dark..bright' },
      decay: { def: 0.5, doc: 'amp decay 30 ms..2 s when triggered' },
      level: { def: 0.7, doc: 'generator level (adds to through signal)' },
      trigger: { def: 0, doc: 'retrigger on rising edge; 0 held = continuous for noise sources' },
    },
  },
  'Drone String': {
    implemented: true,
    params: {
      pitch: { def: 0.4, doc: 'root note of the string set (~E1..E4)' },
      tone: { def: 0.5, doc: 'string brightness (KS damping)' },
      decay: { def: 0.6, doc: 'string sustain' },
      sens: { def: 0.6, doc: 'input→excitation sensitivity' },
      lfoSpeed: { def: 0.3, doc: 'strum/arpeggio LFO across the strings 0.1..8 Hz (synced when program syncs)' },
    },
  },
  'Chord Resonator': {
    implemented: true,
    params: {
      key: { def: 0.5, doc: 'chord root, chromatic C3..C5 (stepped, per KPV Chord_Reso_Key)' },
      tone: { def: 0.5, doc: 'resonator brightness' },
      decay: { def: 0.5, doc: 'resonance decay time' },
      depth: { def: 0.8, doc: 'dry/resonated mix inside the unit' },
    },
  },
  'Vocal FX': {
    implemented: true,
    params: {
      harmony: { def: 0.5, doc: 'harmony interval sweep (−12..+12 st around unison, scale-snapped)' },
      formant: { def: 0.5, doc: 'formant shift down..up (0.5 = neutral)' },
      root: { def: 0, doc: 'scale root sweep (e.g. C..G for Hardtune C-G programs)' },
      amount: { def: 1, doc: 'pitch-correction strength 0..1 (1 = hard tune)' },
      mix: { def: 1, doc: 'dry/tuned balance inside the unit' },
    },
  },
};

export const IMPLEMENTED_UNITS = Object.keys(UNIT_CATALOG).filter(
  (k) => UNIT_CATALOG[k]!.implemented
);
